import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ExternalLink, Shield, Loader2, AlertTriangle, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SafeBrowser = ({ url, title: initialTitle, onClose, token }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageTitle, setPageTitle] = useState(initialTitle || '');
  const [resolvedUrl, setResolvedUrl] = useState(url);
  const iframeRef = useRef(null);

  const loadPage = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const resp = await fetch(
        `${API_URL}/api/proxy/page?url=${encodeURIComponent(url)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!resp.ok) {
        throw new Error(`Server returned ${resp.status}`);
      }

      const data = await resp.json();

      if (!data.success) {
        setError(data);
        setLoading(false);
        return;
      }

      setPageTitle(data.title || initialTitle);
      setResolvedUrl(data.url || url);

      // Write sanitized HTML into the sandboxed iframe
      const iframe = iframeRef.current;
      if (iframe) {
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.open();
        doc.write(data.html);
        doc.close();
      }

      setLoading(false);
    } catch (err) {
      setError({
        error: 'fetch_failed',
        message: 'Could not load this page through Safe Browser.',
      });
      setLoading(false);
    }
  }, [url, token, initialTitle]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const displayDomain = (() => {
    try {
      return new URL(resolvedUrl).hostname;
    } catch {
      return resolvedUrl;
    }
  })();

  return (
    <div
      className="safe-browser-overlay"
      data-testid="safe-browser-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="safe-browser-container" data-testid="safe-browser-container">
        {/* Top bar */}
        <div className="safe-browser-toolbar" data-testid="safe-browser-toolbar">
          <div className="safe-browser-toolbar-left">
            <div className="safe-browser-shield" data-testid="safe-browser-shield">
              <Shield className="h-4 w-4" />
              <span>Safe Browser</span>
            </div>
            <div className="safe-browser-url" data-testid="safe-browser-url">
              <Lock className="h-3 w-3 opacity-50" />
              <span className="safe-browser-domain">{displayDomain}</span>
              <span className="safe-browser-path" title={resolvedUrl}>
                {(() => {
                  try {
                    const u = new URL(resolvedUrl);
                    const path = u.pathname + u.search;
                    return path.length > 60 ? path.substring(0, 57) + '...' : path;
                  } catch {
                    return '';
                  }
                })()}
              </span>
            </div>
          </div>
          <div className="safe-browser-toolbar-right">
            <a
              href={resolvedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="safe-browser-visit-btn"
              data-testid="safe-browser-visit-original"
              title="Open original site in new tab"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              <span>Visit Original</span>
            </a>
            <button
              onClick={onClose}
              className="safe-browser-close-btn"
              data-testid="safe-browser-close"
              title="Close Safe Browser"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content area */}
        <div className="safe-browser-content">
          {loading && (
            <div className="safe-browser-loading" data-testid="safe-browser-loading">
              <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
              <p className="text-gray-600 mt-3 text-sm">Loading through Safe Browser...</p>
              <p className="text-gray-400 text-xs mt-1">Stripping trackers and scripts</p>
            </div>
          )}

          {error && (
            <div className="safe-browser-error" data-testid="safe-browser-error">
              <AlertTriangle className="h-10 w-10 text-amber-500" />
              <h3 className="text-lg font-semibold text-gray-800 mt-3">Can't display this page</h3>
              <p className="text-gray-500 text-sm mt-1 max-w-md text-center">
                {error.message || 'This site requires direct access to function properly.'}
              </p>
              <a
                href={resolvedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="safe-browser-fallback-btn"
                data-testid="safe-browser-fallback"
              >
                <ExternalLink className="h-4 w-4" />
                Open in new tab instead
              </a>
            </div>
          )}

          <iframe
            ref={iframeRef}
            className="safe-browser-iframe"
            sandbox="allow-same-origin"
            title={pageTitle || 'Safe Browser'}
            data-testid="safe-browser-iframe"
            style={{ display: loading || error ? 'none' : 'block' }}
          />
        </div>

        {/* Bottom info bar */}
        <div className="safe-browser-footer" data-testid="safe-browser-footer">
          <Shield className="h-3 w-3 text-emerald-500" />
          <span>Protected view — scripts, trackers, and cookies are blocked</span>
        </div>
      </div>
    </div>
  );
};

export default SafeBrowser;
